// Nạp từ AppSheet mọi dữ liệu một kỳ lương cần, rồi dựng sẵn bằng các hàm thuần trong core/periodInputs.
import authUtils from '../../utils/authUtils';
import { canonicalPayrollCode } from '../core/domain';
import { calculateProductivityLines } from '../core/calculation';
import { calculateProductivityReport, toPayrollProductivityRows } from '../core/productivity';
import {
    assembleTemplates, buildTeamsByEmployee, checkConstantValues, isEmployeeInPeriod,
    resolveAgreementsAt, resolveDatedValuesAt, selectPeriodAdvances,
} from '../core/periodInputs';
import { payrollRepos } from './tables';
import { toSheetDate } from './sheetFormat';

/**
 * Bảng của giai đoạn 6 (lịch ngày lễ, đơn phép) có thể chưa được tạo trên AppSheet: thiếu bảng thì coi như
 * chưa có ngày lễ/đơn phép và báo một cảnh báo, chứ không làm hỏng cả màn bảng lương.
 */
async function listOptional(repo, label) {
    try {
        return { rows: await repo.list(), warning: null };
    } catch (error) {
        console.error(`Không đọc được bảng ${repo.schema.table}`, error);
        return { rows: [], warning: { code: 'MISSING_TABLE', blocking: false, message: `Chưa đọc được bảng ${repo.schema.table} nên ${label} tính bằng 0. Kiểm tra bảng đã được thêm vào AppSheet chưa.` } };
    }
}

// Ô sheet đọc không được, gom theo bảng + cột để màn hình báo một dòng cho mỗi cột hỏng.
export function collectSheetIssues(...rowLists) {
    const grouped = new Map();
    for (const rows of rowLists) {
        for (const row of rows) {
            for (const issue of row._issues ?? []) {
                const key = `${issue.table}|${issue.column}`;
                const group = grouped.get(key) ?? { ...issue, count: 0, samples: [] };
                group.count += 1;
                if (group.samples.length < 3) group.samples.push(String(issue.value));
                grouped.set(key, group);
            }
        }
    }
    return Array.from(grouped.values()).map((group) => ({
        code: 'SHEET_FORMAT',
        blocking: false,
        message: `${group.table}, cột ${group.column}: ${group.count} ô không đọc được (ví dụ: ${group.samples.join(', ')}).`,
    }));
}

/**
 * Dữ liệu nền của kỳ: danh mục biến, hằng số và công thức hiệu lực tại cuối kỳ, mẫu, thỏa thuận, nhân
 * sự làm việc trong kỳ và tổ của họ, phiếu ứng của kỳ.
 */
export async function loadPayrollReferenceData(range, periodId) {
    const [variables, constants, formulas, templates, templateColumns, agreements, employees, allocations, advances, holidays, leaves] = await Promise.all([
        payrollRepos.variables.list(),
        payrollRepos.constants.list(),
        payrollRepos.formulas.list(),
        payrollRepos.templates.list(),
        payrollRepos.templateColumns.list(),
        payrollRepos.agreements.list(),
        payrollRepos.employees.list(),
        payrollRepos.allocations.list(),
        payrollRepos.advances.list(),
        listOptional(payrollRepos.holidays, 'ngày lễ (NL)'),
        listOptional(payrollRepos.leaveRequests, 'ngày phép (PN)'),
    ]);
    const overlapsPeriod = (row) => row.TU_NGAY && row.TU_NGAY <= range.to && (row.DEN_NGAY ?? row.TU_NGAY) >= range.from;
    const variableCatalog = variables.filter((variable) => variable.HOAT_DONG).sort((left, right) => (left.THU_TU ?? 0) - (right.THU_TU ?? 0));
    const constantRows = resolveDatedValuesAt(constants, range.to);
    const employeesInPeriod = employees
        .filter((employee) => employee.SO_HD && isEmployeeInPeriod(employee, range))
        .sort((left, right) => left.HO_TEN.localeCompare(right.HO_TEN, 'vi'));
    return {
        range,
        variableCatalog,
        variableByCode: new Map(variableCatalog.map((variable) => [canonicalPayrollCode(variable.MA_BIEN), variable])),
        templates: assembleTemplates(templates, templateColumns),
        employees: employeesInPeriod,
        teamsByEmployee: buildTeamsByEmployee(employeesInPeriod, allocations, range.to),
        base: {
            agreements,
            agreementByEmployee: resolveAgreementsAt(agreements, range.to),
            advances: selectPeriodAdvances(advances, range, periodId),
            constantRows,
            formulaRows: resolveDatedValuesAt(formulas, range.to),
            // Chỉ ngày lễ đã xác nhận và đơn đã duyệt mới vào lương; lọc sẵn theo kỳ để chữ ký kỳ không đổi vì dòng ngoài kỳ.
            holidays: holidays.rows.filter((row) => row.HIEU_LUC !== false && row.DA_XAC_NHAN && overlapsPeriod(row)),
            leaveRequests: leaves.rows.filter((row) => row.TRANG_THAI === 'DA_DUYET' && overlapsPeriod(row)),
        },
        warnings: [
            ...checkConstantValues(constantRows, variableCatalog),
            ...[holidays.warning, leaves.warning].filter(Boolean),
            ...collectSheetIssues(variables, constants, formulas, templates, templateColumns, agreements, employees, advances, holidays.rows, leaves.rows),
        ],
    };
}

/**
 * Năng suất của kỳ theo đúng phép tính của báo cáo năng suất, đổi thành dòng lương theo SỐ HĐ người phụ
 * trách. Dòng chưa gắn SỐ HĐ (máy, đội, hoặc chưa ghép) trả về riêng để cảnh báo.
 */
export async function loadPeriodProductivity(range) {
    const [nhapCt, toPbnsRaw, nhapBaoBi, xuatNhapKhoCt, congDoanDonGias, hieuChinhList] = await Promise.all([
        authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {}),
        authUtils.apiRequest('TO_PBNS', 'Find', {}),
        authUtils.apiRequest('NHAPBAOBI', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {}),
        authUtils.apiRequest('CONGDOAN_DONGIA', 'Find', {}),
        authUtils.apiRequest('HIEU_CHINH_TIEN_NANG_SUAT', 'Find', {}),
    ]);
    const report = calculateProductivityReport({
        nhapCt, toPbnsRaw, nhapBaoBi, xuatNhapKhoCt, congDoanDonGias, hieuChinhList,
        fromDate: toSheetDate(range.from),
        toDate: toSheetDate(range.to),
    });
    const rows = toPayrollProductivityRows(report.dataByNhom);
    const { lines, unmatchedRecords } = calculateProductivityLines(rows);
    const linesByEmployee = new Map();
    for (const line of lines) linesByEmployee.set(line.NHAN_SU_ID, [...(linesByEmployee.get(line.NHAN_SU_ID) ?? []), line]);
    return {
        linesByEmployee,
        unmatchedRecords,
        mergedRows: rows.filter((row) => row.mergedAllocationIds.length),
    };
}
